import { useAppDispatch, useAppSelector } from 'app/hooks';
import store from 'app/store';
import clsx from 'clsx';
import Modal from 'components/common/modal';
import SystemsTable from 'components/equipment-page/systems-table';
import { useUpsertCustomSystemMutation } from 'features/api/project-api';
import { setConfigurations } from 'features/configurations';
import {
  bumpCustomSystemsRevision,
  setSystemsForNewOption
} from 'features/systems';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { markCondensersStale } from 'utils/configurations';
import { buildCustomSystemEntries } from 'utils/systems';
import { notification } from 'utils/ui';

type SystemsListModalProps = Readonly<{
  openState: [boolean, Dispatch<SetStateAction<boolean>>];
  initialMode?: 'matching' | 'valid';
}>;

export default function SystemsListModal({
  openState,
  initialMode
}: SystemsListModalProps) {
  const dispatch = useAppDispatch();
  const configurations = useAppSelector(
    (state) => state.configurations.configurations
  );
  const activeConfig = useAppSelector(
    (state) => state.configurations.activeConfig
  );
  const systemOptions = useAppSelector((state) => state.systems.systemOptions);
  const systemsForNewOption = useAppSelector(
    (state) => state.systems.systemsForNewOption
  );

  const [upsertCustomSystem, { isLoading: isSaving }] =
    useUpsertCustomSystemMutation();

  const [mode, setMode] = useState<'matching' | 'valid'>(
    initialMode ?? 'matching'
  );

  useEffect(() => {
    if (openState[0]) {
      setMode(initialMode ?? 'matching');
    }
  }, [openState[0], initialMode]);

  const activeConfiguration = configurations[activeConfig];
  const activeConfigId = activeConfiguration?.uniqueId;
  const configOptions = useMemo(
    () => systemOptions[activeConfigId] ?? [],
    [systemOptions, activeConfigId]
  );

  const placedCondensers = useMemo(() => {
    if (!activeConfiguration) return [];
    return activeConfiguration.condensers.filter(
      (c) => c.position !== undefined
    );
  }, [activeConfiguration]);

  const stableCondensers = useMemo(() => {
    return placedCondensers.map((condenser) => {
      if (mode === 'valid') return condenser;
      return {
        ...condenser,
        validSystems: condenser.validSystems.filter(
          (s) => s.isMatchingAirHandlers
        )
      };
    });
  }, [placedCondensers, mode]);

  const allCondensersSelected = useMemo(
    () =>
      placedCondensers.length > 0 &&
      placedCondensers.every((c) =>
        systemsForNewOption.some((s) => s.condenserUnique === c.uniqueId)
      ),
    [placedCondensers, systemsForNewOption]
  );

  const handleSave = useCallback(async () => {
    if (!activeConfiguration || !allCondensersSelected) return;

    const existingNames = new Set(
      configOptions
        .filter(
          (o) => o.configuration.uniqueId === activeConfiguration.uniqueId
        )
        .map((o) => o.name)
    );

    let systemNumber = 1;
    while (existingNames.has(`Custom System ${systemNumber}`)) {
      systemNumber += 1;
    }
    const name = `Custom System ${systemNumber}`;
    const entries = buildCustomSystemEntries(
      systemsForNewOption,
      activeConfiguration
    );

    try {
      await upsertCustomSystem({
        configurationUniqueId: activeConfiguration.uniqueId,
        name,
        entries
      }).unwrap();

      dispatch(
        bumpCustomSystemsRevision({ configId: activeConfiguration.uniqueId })
      );

      const latest = store.getState().configurations.configurations;
      dispatch(
        setConfigurations(
          latest.map((c) =>
            c.uniqueId === activeConfiguration.uniqueId
              ? markCondensersStale(c)
              : c
          )
        )
      );

      dispatch(setSystemsForNewOption([]));
      notification('System option created', 'success');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to save custom system', err);
      notification('Failed to save custom system', 'error');
    }
  }, [
    activeConfiguration,
    allCondensersSelected,
    configOptions,
    systemsForNewOption,
    dispatch,
    upsertCustomSystem
  ]);

  const handleClose = useCallback(() => {
    dispatch(setSystemsForNewOption([]));
  }, [dispatch]);

  return (
    <Modal
      openState={openState}
      title='Create Custom System'
      size='fullscreen'
      buttonText='Create System Option'
      saveDisabled={!allCondensersSelected || isSaving}
      onSave={handleSave}
      onClose={handleClose}
    >
      <div className='flex flex-col gap-6'>
        <div className='flex border-b border-gray-200'>
          <button
            type='button'
            onClick={() => setMode('matching')}
            className={clsx(
              'px-4 py-2 text-sm font-medium transition-colors',
              mode === 'matching'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            Matching Systems
          </button>
          <button
            type='button'
            onClick={() => setMode('valid')}
            className={clsx(
              'px-4 py-2 text-sm font-medium transition-colors',
              mode === 'valid'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            Valid Systems
          </button>
        </div>

        {stableCondensers.map((condenser, index) =>
          condenser.validSystems.length === 0 ? null : (
            <SystemsTable
              key={condenser.uniqueId}
              condenser={condenser}
              condenserIndex={index}
              totalCondensers={stableCondensers.length}
              selectable
            />
          )
        )}
        {stableCondensers.every((c) => c.validSystems.length === 0) && (
          <div className='flex items-center justify-center py-8 text-sm text-gray-500'>
            No systems available.
          </div>
        )}
      </div>
    </Modal>
  );
}
